import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { PageRegistryEntry } from "../analytics/pageRegistry";
import { trackProductPageView } from "../analytics/product";
import {
  buildProductBaseFromRegistry,
  useProductAnalyticsContext,
} from "./useProductAnalyticsContext";

type UseProductPageViewParams = {
  registryEntry: PageRegistryEntry | null;
  entityIds?: Record<string, string | undefined>;
};

export const useProductPageView = ({
  registryEntry,
  entityIds,
}: UseProductPageViewParams): void => {
  const location = useLocation();
  const context = useProductAnalyticsContext();
  const lastTrackedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!registryEntry) return;

    const entityKey = entityIds
      ? Object.entries(entityIds)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => `${key}=${value ?? ""}`)
          .join("|")
      : "";

    const trackKey = [
      registryEntry.pageKey,
      location.pathname,
      location.search,
      entityKey,
    ].join("|");

    if (lastTrackedKeyRef.current === trackKey) {
      return;
    }

    lastTrackedKeyRef.current = trackKey;

    trackProductPageView(
      buildProductBaseFromRegistry(registryEntry, context, location),
      {
        page_url: context.page_url,
        attribution_source: context.source,
        utm_source: context.utm_source ?? null,
        utm_campaign: context.utm_campaign ?? null,
        invite_code_present: Boolean(context.invite_code),
        qr_session_id: context.qr_session_id ?? null,
        ...entityIds,
      },
    );
  }, [context, entityIds, location, registryEntry]);
};
